import AsyncStorage from '@react-native-async-storage/async-storage';
import { logger } from '@/lib/logger';
import { markVideoCompleted, updateVideoLeftOffTime } from '@/supabase/services/userVideoInteractions';
import type { UserLessonVideoInteraction } from '@/types';
import type { QueuedProgressEntry } from './types';

/**
 * Progress writes made while offline (resume position, mark complete) are
 * queued here and replayed when connectivity returns. Only the latest entry
 * per (video, type) is kept.
 */
const STORAGE_KEY = 'osj.offline.progressQueue.v1';

let queue: QueuedProgressEntry[] | null = null;
let flushing = false;

async function load(): Promise<QueuedProgressEntry[]> {
  if (queue) return queue;
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    queue = raw ? (JSON.parse(raw) as QueuedProgressEntry[]) : [];
  } catch (err) {
    logger.error('[offline] progress queue load failed', err);
    queue = [];
  }
  return queue;
}

async function save(next: QueuedProgressEntry[]): Promise<void> {
  queue = next;
  try {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch (err) {
    logger.error('[offline] progress queue save failed', err);
  }
}

function sameSlot(a: QueuedProgressEntry, b: QueuedProgressEntry): boolean {
  return a.type === b.type && a.userId === b.userId && a.lessonVideoId === b.lessonVideoId;
}

async function enqueue(entry: QueuedProgressEntry): Promise<void> {
  const current = await load();
  await save([...current.filter((e) => !sameSlot(e, entry)), entry]);
}

export function enqueueLeftOff(userId: string, lessonVideoId: number, lessonId: number, seconds: number): Promise<void> {
  return enqueue({ type: 'left_off', userId, lessonVideoId, lessonId, seconds, at: new Date().toISOString() });
}

export function enqueueCompleted(
  userId: string,
  lessonVideoId: number,
  lessonId: number,
  completed: boolean,
): Promise<void> {
  return enqueue({ type: 'completed', userId, lessonVideoId, lessonId, completed, at: new Date().toISOString() });
}

export async function getQueuedProgress(userId: string): Promise<QueuedProgressEntry[]> {
  return (await load()).filter((e) => e.userId === userId);
}

/**
 * Overlays queued (not yet synced) progress on a user_videos list so the
 * offline lesson page resumes where the user actually left off.
 */
export function applyQueuedProgress(
  userVideos: UserLessonVideoInteraction[],
  queued: QueuedProgressEntry[],
): UserLessonVideoInteraction[] {
  if (queued.length === 0) return userVideos;
  const byVideo = new Map(userVideos.map((uv) => [uv.lesson_video_id, { ...uv }]));
  for (const entry of queued) {
    const existing = byVideo.get(entry.lessonVideoId) ?? {
      id: '',
      user_id: entry.userId,
      lesson_video_id: entry.lessonVideoId,
      lesson_id: entry.lessonId,
      is_opened: true,
      is_completed: false,
      left_off_at_seconds: 0,
      created_at: entry.at,
      updated_at: entry.at,
    };
    if (entry.type === 'left_off') existing.left_off_at_seconds = entry.seconds;
    else existing.is_completed = entry.completed;
    existing.updated_at = entry.at;
    byVideo.set(entry.lessonVideoId, existing);
  }
  return [...byVideo.values()];
}

/** Replays the queue against Supabase. Safe to call repeatedly. */
export async function flushProgressQueue(): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    const entries = await load();
    if (entries.length === 0) return;
    const remaining: QueuedProgressEntry[] = [];
    for (const entry of entries) {
      try {
        if (entry.type === 'left_off') {
          await updateVideoLeftOffTime(entry.userId, entry.lessonVideoId, entry.seconds, entry.lessonId);
        } else {
          await markVideoCompleted(entry.userId, entry.lessonVideoId, entry.lessonId, entry.completed);
        }
      } catch (err) {
        logger.warn('[offline] progress replay failed, keeping entry', { entry, err });
        remaining.push(entry);
      }
    }
    await save(remaining);
    if (remaining.length < entries.length) {
      logger.info('[offline] progress queue flushed', { synced: entries.length - remaining.length, remaining: remaining.length });
    }
  } finally {
    flushing = false;
  }
}

/** Test-only. */
export function __resetProgressQueueForTests(): void {
  queue = null;
  flushing = false;
}
